import React, { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowUpRight, Clock, Share2 } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import LiveChat from '../components/LiveChat';
import { getPostBySlug, blogPosts } from '../data/blog';
import { openCalendly } from '../lib/calendly';

// Renders **bold** markdown inline
function renderInline(text) {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="text-white font-semibold">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return <React.Fragment key={i}>{part}</React.Fragment>;
  });
}

function Block({ block }) {
  switch (block.type) {
    case 'h2':
      return (
        <h2 className="font-display text-3xl md:text-4xl font-bold text-white mt-16 mb-6 leading-tight">
          {block.text}
        </h2>
      );
    case 'h3':
      return (
        <h3 className="font-display text-2xl font-bold text-white mt-10 mb-4">
          {block.text}
        </h3>
      );
    case 'p':
      return (
        <p className="text-lg text-slate-300 leading-[1.85] mb-6">
          {renderInline(block.text)}
        </p>
      );
    case 'ul':
      return (
        <ul className="space-y-3 mb-8 mt-2">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-3 text-lg text-slate-300 leading-relaxed">
              <span className="mt-3 w-1.5 h-1.5 rounded-full bg-emerald-400 flex-shrink-0" />
              <span>{renderInline(item)}</span>
            </li>
          ))}
        </ul>
      );
    case 'ol':
      return (
        <ol className="space-y-4 mb-8 mt-2 counter-reset-item">
          {block.items.map((item, i) => (
            <li key={i} className="flex items-start gap-4 text-lg text-slate-300 leading-relaxed">
              <span className="font-display font-bold text-emerald-400 text-xl flex-shrink-0 min-w-[1.75rem]">
                {String(i + 1).padStart(2, '0')}
              </span>
              <span>{renderInline(item)}</span>
            </li>
          ))}
        </ol>
      );
    case 'pullquote':
      return (
        <blockquote className="my-14 pl-8 border-l-2 border-emerald-400">
          <p className="font-display text-2xl md:text-3xl font-bold text-white leading-snug">
            &ldquo;{block.text}&rdquo;
          </p>
        </blockquote>
      );
    default:
      return null;
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) return <Navigate to="/blog" replace />;

  const otherPosts = blogPosts.filter((p) => p.slug !== post.slug).slice(0, 3);

  const handleShare = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: post.title, url }); } catch { /* cancelled */ }
    } else {
      try { await navigator.clipboard.writeText(url); } catch { /* ignored */ }
    }
  };

  return (
    <div className="bg-[#050A08] min-h-screen">
      <Header />
      <main className="pt-32 pb-28">
        {/* Cover */}
        <div className="relative h-[46vh] md:h-[62vh] overflow-hidden">
          <img src={post.coverImage} alt={post.title} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#050A08]/50 via-[#050A08]/70 to-[#050A08]" />
        </div>

        {/* Article header */}
        <article className="max-w-3xl mx-auto px-6 lg:px-8 -mt-40 md:-mt-56 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <Link
              to="/blog"
              className="flex w-fit items-center gap-2 text-sm text-emerald-400 hover:text-emerald-300 transition-colors duration-300 mb-8"
              data-testid="back-to-blog"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Journal
            </Link>

            <span className="inline-block text-emerald-400 font-semibold text-sm uppercase tracking-[0.25em] mb-5">
              {post.category}
            </span>

            <h1 className="font-display text-4xl md:text-6xl font-black text-white leading-[0.98] tracking-tight mb-6">
              {post.title}
            </h1>
            {post.subtitle && (
              <p className="text-xl md:text-2xl text-slate-400 leading-snug mb-10">
                {post.subtitle}
              </p>
            )}

            <div className="flex items-center justify-between gap-4 pb-10 mb-12 border-b border-white/[0.08]">
              <div className="flex items-center gap-4 flex-wrap">
                <div className="w-11 h-11 rounded-full bg-emerald-500/15 border border-emerald-500/25 flex items-center justify-center font-display font-bold text-emerald-400">
                  OH
                </div>
                <div>
                  <div className="text-white font-semibold text-sm">{post.author}</div>
                  <div className="text-slate-500 text-xs flex items-center gap-3">
                    <span>{post.date}</span>
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{post.readTime}</span>
                  </div>
                </div>
              </div>
              <button
                onClick={handleShare}
                className="w-11 h-11 rounded-full bg-white/[0.04] border border-white/10 hover:bg-emerald-500 hover:border-emerald-500 flex items-center justify-center text-slate-400 hover:text-[#04120C] transition-all duration-300"
                data-testid="share-post"
                aria-label="Share post"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
          </motion.div>

          {/* Body */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            data-testid="post-body"
          >
            {post.body.map((block, i) => (
              <Block key={i} block={block} />
            ))}
          </motion.div>

          {/* End CTA */}
          <div className="mt-20 p-8 md:p-10 rounded-3xl bg-[#0D1612] border border-emerald-500/20">
            <h3 className="font-display text-2xl md:text-3xl font-bold text-white mb-3">
              Ready to reclaim your time?
            </h3>
            <p className="text-slate-400 leading-relaxed mb-6">
              Book a free 30-minute consultation and we&rsquo;ll match you with a handpicked virtual assistant &mdash; no obligations.
            </p>
            <button
              onClick={openCalendly}
              className="group inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-[#04120C] px-7 py-4 rounded-full font-bold transition-all duration-300 hover:shadow-[0_0_40px_rgba(16,185,129,0.5)]"
              data-testid="post-cta"
            >
              Book a Free Consultation
              <ArrowUpRight className="w-5 h-5 group-hover:rotate-45 transition-transform duration-300" />
            </button>
          </div>
        </article>

        {/* More posts */}
        {otherPosts.length > 0 && (
          <section className="max-w-7xl mx-auto px-6 lg:px-12 mt-28">
            <h2 className="font-display text-3xl md:text-4xl font-bold text-white mb-10">Keep reading</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {otherPosts.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`} className="group block rounded-2xl overflow-hidden bg-[#0D1612] border border-white/[0.06] hover:border-emerald-500/40 transition-colors duration-500">
                  <div className="h-40 overflow-hidden">
                    <img src={p.coverImage} alt={p.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  </div>
                  <div className="p-6">
                    <span className="inline-block text-emerald-400 text-xs uppercase tracking-[0.2em] font-semibold mb-3">{p.category}</span>
                    <h3 className="font-display text-lg font-bold text-white leading-snug group-hover:text-emerald-300 transition-colors duration-300">{p.title}</h3>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>
      <Footer />
      <LiveChat />
    </div>
  );
}
